import React, { useEffect, useRef, useState, useMemo } from 'react';
import {
  resolveImageUrl,
  toPlaceholderSvgDataUri,
} from '../../utils/mediaPolicy';
import runtimeFlags from '../../config/runtimeFlags';

/*
  SmartImage
  - Centralized block via runtimeFlags.blockExternalImages (no network in dev if true)
  - IntersectionObserver lazy load
  - Placeholder fallback on error
*/
export default function SmartImage({
  src,
  alt = '',
  size = 88,
  style = {},
  className,
  priority = false, // true for above-the-fold icons
  decoding = 'async',
  fetchpriority, // 'high' | 'low'
  loading, // 'eager' | 'lazy'
  ...imgProps // ← forward any extra props (e.g., crossOrigin)
}) {
  // Resolve URL through media policy (may rewrite src and/or mark blocked)
  const policy = useMemo(
    () => resolveImageUrl(src, { alt, size }),
    [src, alt, size],
  );
  const url = policy?.src || src || '';
  const placeholder = useMemo(
    () => toPlaceholderSvgDataUri(alt || 'image', size),
    [alt, size],
  );

  // Determine if the final URL is external and extract hostname
  const { isExternal, host } = useMemo(() => {
    try {
      const u = new URL(String(url || ''), window.location.origin);
      const external =
        !!url &&
        u.origin !== window.location.origin &&
        !String(url).startsWith('/');
      return { isExternal: external, host: u.hostname };
    } catch {
      const s = String(url || '');
      return { isExternal: /^https?:\/\//i.test(s), host: '' };
    }
  }, [url]);

  const allowlist = runtimeFlags.imageHostAllowlist || [];
  const devBlock =
    !!runtimeFlags.blockExternalImages &&
    isExternal &&
    !allowlist.includes(host);
  const blocked = policy?.blocked === true || devBlock;

  // Debug toggle: ?dbg=img or runtimeFlags.debugBadges
  const dbg =
    runtimeFlags.debugBadges ||
    (typeof window !== 'undefined' &&
      /(?:^|[?&])dbg=img(?:=1)?(?:&|$)/i.test(window.location.search || ''));

  useEffect(() => {
    if (!dbg) return;
    // eslint-disable-next-line no-console
    // console.log('[SmartImage]', {
    //   srcProp: src,
    //   policySrc: policy?.src,
    //   url,
    //   isExternal,
    //   host,
    //   blockExternalImages: runtimeFlags.blockExternalImages,
    //   allowlist,
    //   devBlock,
    //   policyBlocked: policy?.blocked === true,
    //   finalBlocked: blocked,
    // });
  }, [dbg, src, policy, url, isExternal, host, devBlock, blocked, allowlist]);

  // Lazy-load unless priority or blocked (no need to observe when blocked)
  const [inView, setInView] = useState(priority || blocked);
  const [error, setError] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (priority || blocked) return;
    const el = ref.current;
    if (!el || !('IntersectionObserver' in window)) {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setInView(true);
            io.disconnect();
            break;
          }
        }
      },
      { root: null, rootMargin: '300px', threshold: 0.01 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [priority, blocked]);

  // Final src selection with proper fallbacks
  const finalSrc = error
    ? placeholder
    : inView
    ? blocked
      ? placeholder
      : url || placeholder
    : placeholder;

  return (
    <img
      ref={ref}
      src={finalSrc}
      alt={alt}
      width={size}
      height={size}
      loading={loading || (priority ? 'eager' : 'lazy')}
      decoding={decoding}
      fetchpriority={fetchpriority || (priority ? 'high' : 'low')}
      style={style}
      className={className}
      onError={() => setError(true)}
      {...imgProps} // forward crossOrigin, etc.
    />
  );
}
